import { defineProperty as _defineProperty, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import classNames from 'classnames';
import warning from 'warning';
import PropTypes from 'prop-types';
import getAnchorProps from './get-anchor-props.js';
import getButtonProps from './get-button-props.js';
import styles from './plain.module.scss';
var Plain = React.forwardRef(function (props, ref) {
  var _classNames2;

  warning(props.children || props.accessibilityLabel || props.iconLeft && props.children, 'The prop `accessibilityLabel` must be provided to the button or link if `iconLeft` or `iconRight` are provided but `children` is not. This helps users on screen readers navigate our content.');
  var children = props.children;

  if (props.iconLeft || props.iconRight) {
    var _classNames;

    children = React.createElement("span", {
      className: styles.flexCenter
    }, props.iconLeft, props.children && React.createElement("span", {
      className: classNames((_classNames = {}, _defineProperty(_classNames, styles.textContainer, true), _defineProperty(_classNames, styles.textContainerLeft, props.iconLeft), _defineProperty(_classNames, styles.textContainerRight, props.iconRight), _classNames))
    }, props.children), props.iconRight);
  }

  var commonProps = {
    disabled: props.isDisabled,
    children: children,
    className: classNames((_classNames2 = {}, _defineProperty(_classNames2, styles.plain, true), _defineProperty(_classNames2, styles.plainThemePrimary, props.theme === 'primary'), _defineProperty(_classNames2, styles.plainThemeSecondary, props.theme === 'secondary'), _defineProperty(_classNames2, styles.plainThemeTertiary, props.theme === 'tertiary'), _defineProperty(_classNames2, styles.plainThemeInherit, props.theme === 'inherit'), _classNames2)),
    'aria-label': props.accessibilityLabel,
    'data-test': props.dataTest,
    ref: ref
  };

  if (props.to) {
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    return React.createElement("a", _extends({}, commonProps, getAnchorProps(props)));
  } // eslint-disable-next-line react/button-has-type


  return React.createElement("button", _extends({}, commonProps, getButtonProps(props)));
});
Plain.propTypes = {
  isDisabled: PropTypes.bool,

  /**
   * Page to navigate to when the anchor is clicked.
   */
  to: PropTypes.string,

  /**
   * Icon from [Thumbprint Icons](/icons/) to render left of the text.
   */
  iconLeft: PropTypes.node,

  /**
   * Icon from [Thumbprint Icons](/icons/) to render right of the text.
   */
  iconRight: PropTypes.node,

  /**
   * Sets the text color.
   */
  theme: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'inherit']),

  /**
   * Opens the URL in a new tab when clicked.
   */
  shouldOpenInNewTab: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,

  /**
   * Function that runs when the user hovers on the button.
   */
  onMouseEnter: PropTypes.func,

  /**
   * Function that runs when the user hovers on the button. Unlike `onMouseEnter`, `onMouseOver`
   * fires each time a child element receives focus.
   */
  onMouseOver: PropTypes.func,

  /**
   * Function that runs when the button receives focus.
   */
  onFocus: PropTypes.func,

  /**
   * Function that runs when the user hovers away from the button.
   */
  onMouseLeave: PropTypes.func,

  /**
   * Function that runs when the button loses focus.
   */
  onBlur: PropTypes.func,

  /**
   * Description of the button’s content. It is required if the button has an icon and no
   * descriptive text.
   */
  accessibilityLabel: PropTypes.string,

  /**
   * A selector hook into the React component for use in automated testing environments.
   */
  dataTest: PropTypes.string
};
Plain.defaultProps = {
  children: undefined,
  isDisabled: false,
  iconLeft: null,
  to: null,
  theme: 'primary',
  shouldOpenInNewTab: false,
  onClick: null,
  onMouseEnter: undefined,
  onMouseOver: undefined,
  onFocus: undefined,
  onMouseLeave: undefined,
  onBlur: undefined,
  accessibilityLabel: undefined,
  dataTest: undefined
};
export default Plain;